import type { BaseTranslation } from 'src/i18n/i18n-types';
import { CompanyType } from 'src/pages/SettingsV2/const';
import { assets } from './contexts/assets';
import { brands } from './contexts/brands';
import { briefs } from './contexts/briefs';
import { comments } from './contexts/comments';
import { cta } from './contexts/cta';
import { directories } from './contexts/directories';
import { general } from './contexts/general';
import { insights } from './contexts/insights';
import { integrations } from './contexts/integrations';
import { library } from './contexts/library';
import { login } from './contexts/login';
import { navigation } from './contexts/navigation';
import { notifications } from './contexts/notifications';
import { onboarding } from './contexts/onboarding';
import { organisations } from './contexts/organisations';
import { permissions } from './contexts/permissions';
import { profileSettings } from './contexts/profile-settings';
import { slack } from './contexts/slack';
import { template } from './contexts/template';
import { tikTok } from './contexts/tik-tok';
import { workspaceSettings } from './contexts/workspace-settings';
import { productCategories } from './productCategories';
import { youtube } from './contexts/youtube';
import type { UserBillingRole } from '@magicbrief/prisma/generated/client2';
import type { UserIndustry } from '@magicbrief/common';

const roles: Record<UserBillingRole, string> = {
  editor: 'Editor',
  viewer: 'Viewer',
  magicbrief: 'MagicBrief',
  guest: 'Guest',
};

const boardStatus: Record<string, string> = {
  IN_PROGRESS: 'In Progress',
  APPROVED: 'Approved',
  NEEDS_REVIEW: 'Needs Review',
  ARCHIVED: 'Archived',
};

const companyTypes: Record<CompanyType, string> = {
  agency: 'Agency / Consultant',
  brand: 'Brand',
};

const industries: Record<UserIndustry, string> = {
  agency: 'Agency',
  app: 'App',
  beauty: 'Beauty',
  clothing: 'Clothing',
  consumerGoods: 'Consumer Goods',
  education: 'Education',
  electronics: 'Electronics',
  entertainment: 'Entertainment',
  finance: 'Finance',
  fitness: 'Fitness',
  foodBeverage: 'Food / Beverage',
  games: 'Games',
  healthcare: 'Healthcare',
  home: 'Home',
  kids: 'Kids',
  news: 'News',
  other: 'Other',
  pets: 'Pets',
  politics: 'Politics',
  realEstate: 'Real Estate',
  services: 'Services',
  sportsOutdoor: 'Sports / Outdoor',
  transport: 'Transport',
  travel: 'Travel',
  travelAccessories: 'Travel Accessories',
};

const en: BaseTranslation = {
  mb: 'MagicBrief',
  storyboards: {
    newCollection: 'New Collection',
    createConcept: 'Create Concept',
    boards: 'Concepts',
    collections: 'Collections',
    storyboards: 'Concepts',
    savedClips: 'Saved Clips',
    boardCollections: '{name} + {count} other{{count:s}}',
    emptyTitle: 'Nothing to see here',
    empty: 'Try creating a new concept to get started.',
    noAssets: 'No asset to display',
    uploadAsset: 'Upload Asset',
    complete: {
      title: 'No concepts complete',
      description: 'Concepts with a <>Complete<> status will show up here.',
    },
    inProgress: {
      title: 'No concepts in progress',
      description:
        'Concepts with a <>Draft<>, <>Shared<>, or <>Needs Approval<> status will show up here.',
    },
    errors: {
      save: 'Something went wrong while saving your concept',
      addToCollection:
        "Something went wrong and we couldn't add the concept to that collection",
      removeFromCollection:
        "Something went wrong and we weren't able to remove the concept from that collection",
      listboards: "We couldn't retrieve the list of your concepts",
      listCollections:
        "We couldn't retrieve the list of your concept collections",
    },
    simpleStatuses: {
      all: 'All',
      active: 'Active',
      complete: 'Complete',
    },
    fields: {
      script: 'Script',
      action: 'Action',
      asset: 'Asset',
    },
    boardStatus,
    manage: 'Manage Concept',
    status: 'Status',
    deleteBoard: 'Delete Concept',
    deleteFromCollectionConfirm:
      'You can choose to remove this concept just from the collection "{name}", or you can delete it entirely.',
    deleteEntirelyDetail:
      "Deleting a concept will permanently delete it from both your and your team's concepts.",
    createCollection: 'Create Collection',
    removeFromCollectionOnly: 'Only Remove From {collection}',
    numFrames: '{frames} {{frame|frames}}',
    exportBoard: 'Export Concept',
    shareScreenReader: 'Sharing privacy setting for concept {name}',
    share: {
      public: { description: 'Anyone with the link can view this concept.' },
      private: {
        description:
          'Only team members or users with access can view this concept.',
      },
    },
    deleteCollection: 'Delete Collection',
    deleteCollectionConfirm:
      "You can choose to remove this collection and preserve the concepts inside it, or you can delete it and it's associated concepts permanently. Any concepts in this collection that are also in other collections will be preserved in all cases.",
    deleteCollectionAndContents: 'Delete Collection & Delete Concepts',
    deleteCollectionOnly: 'Delete Collection & Preserve Concepts',
    numBoards: '{count} concepts',
    alsoDeleteBoards: 'Delete concepts in this collection',
    duplicateBoard: 'Duplicate Concept',
  },
  search: {
    suggestions: 'Suggestions',
    recent: 'Recently Searched',
    recentEmpty: 'No recent history',
  },
  upgrade: {
    continue: 'Continue',
    upgradeNow: 'Upgrade Now',
    title: 'Unlock the full workflow',
    choosePlan: 'Choose a plan to upgrade',
    currentPlan: 'Current Plan',
    collaboration: 'Collaboration',
    save20: 'Save 20%',
    getXOffYourPlanNow: 'Get {x}% off your plan now',
    payMonthly: 'Pay monthly',
    payYearly: 'Pay yearly',
    paywalledDownloadTitle: 'Upgrade to download ads',
    paywalledAdScoreTitle: 'Upgrade to view Ad Score',
    priceMo: '<>${dollars}<> / month',
    perSeat: '+ ${dollars} per seat',
    perSeatBilled: '+ ${dollars} for additional seats, billed monthly',
    ticketStub: 'Pro Pass',
    description:
      'Upgrade your account and get unlimited access to all of MagicBrief.',
    upgrade: 'Upgrade',
    upgradePlan: 'Upgrade Plan',
    upgradeToX: 'Upgrade to {tier}',
    wouldYouLikeToUpgradeToX: 'Would you like to upgrade your plan to {tier}?',
    upgradeSuccess: 'Plan upgraded successfully',
    upgradeError: 'An error occurred while upgrading your plan',
    proPlanTrial: 'Pro plan trial',
    trialEndsInNDays: 'Trial ends in {n} days',
    trialEndsTomorrow: 'Trial ends tomorrow',
    trialEndsToday: 'Trial ends today',
    trialExpired: 'Your trial has expired',
    savedAds:
      'Keep access to your <>{{saved ads | saved ads | ?? saved ads}}<>', // "saved ads" when 0 or 1, {num} saved ads when > 1
    libraries:
      '<>View and save unlimited ads<> from the MagicBrief Discover library',
    discover: '<>Get winning ads<> in your discovery library updated daily',
    boards:
      '<>Build concepts<> to brief talent, video editors and motion designers',
    joinTeamsBuildingBetterAds: 'Join 1,000+ teams building better ads',
    starRating: '5 star rating',
    briefs:
      '<>Create Briefs<> in a fraction of the time using our modular brief builder',
    viewAllFeatures: 'View all 68 features',
    breakdown:
      '<>Breakdown ads<> using MagicAI, quickly create concepts or new variants',
    proPlan: 'Pro',
    processing: 'Processing payment...',
    alreadyUpgraded: 'Your plan has already been upgraded.',
    errors: {
      noPricing:
        'The checkout is experiencing problems at the moment. Please try again later.',
      incomplete: 'All highlighted fields need to be completed.',
      unknown:
        'An unexpected error occurred. Try reloading the page, or contact support.',
    },
    refundCancelAnytime: 'billed monthly • cancel anytime',
    manageSeats: 'Manage Seats',
    tiers: {
      starter: 'Starter',
      pro: 'Pro',
      team: 'Team',
      enterprise: 'Enterprise',
    },
    chatTo: 'Chat to {name}',
    adminNSupport: 'Admin & Support',
    nBrands: '{n} Brands',
    adsPerMonth: '{n} ads / month',
    lastNDays: 'Last {n} days',
    lastQuarter: 'Last quarter',
    nGB: '{n} GB',
    nSeatsIncluded: '{n} Seats Included',
    trustedByTeamsAt: 'Trusted by teams at',
    paywallBanner: {
      title: 'Get access to all {n} ads',
      description:
        'Get unlimited access to the full library & pro features from just <>${price}/month<> — cancel anytime.',
    },
    features: {
      includedSeats: 'Included Seats',
      extraSeats: 'Extra Seats',
      spaces: 'Spaces',
      viewOnlyGuests: 'View-only guests',
      adLibrary: 'Ad library',
      adPacks: 'Ad collections',
      aiPoweredSearch: 'AI-powered search',
      aiPoweredThemeFiltering: 'AI-powered theme filtering',
      adLongevityData: 'Ad longevity data',
      competitorPerformanceEst: 'Competitor performance estimates',
      brandTracking: 'Brand tracking',
      aiPoweredTranscription: 'AI-powered transcription',
      aiPoweredSearchNFiltering: 'AI-powered search & filtering',
      adAccountConnections: 'Ad account connections',
      dataLookBack: 'Data look back',
      reports: 'Reports',
      slackIntegration: 'Slack integration',
      modularBriefEditor: 'Modular brief editor',
      modularBriefBuilder: 'Modular brief builder',
      contentUpload: 'Content upload',
      aiPoweredScriptGen: 'AI-powered script generation',
      briefTemplates: 'Brief templates',
      storage: 'Storage',
      timelineCommenting: 'Timeline commenting',
      timelineCommentingNReview: 'Timeline commenting & review',
      helpCentre: 'Help centre',
      liveChat: 'Live Chat',
      customMigrations: 'Custom migrations',
      onboardingSupport: '1:1 Onboarding support',
      slackChannel: '1:1 Slack channel',
      saml: 'SAML / Single Sign On',
      customAgreements: 'Custom agreements',
      advancedCreativeAnalytics: {
        title: 'Advanced creative analytics',
        description:
          'Invite team members to collaborate on reports and share ad account insights.',
      },
      adResearchEngine: {
        title: 'The world’s best ad research engine',
        description:
          'Cut your creative research time in half with AI powered search across 12million+ ads.',
      },
      trackAnyBrandRunningAds: {
        title: 'Track any brand running ads',
        description:
          'An always on view of your competitors’ creative strategy throughout the year.',
      },
      automatedSlackReporting: {
        title: 'Automated Slack reporting',
        description:
          'Get weekly performance updates of your account sent straight to Slack.',
      },
      seeWinnersWithRecommendations: {
        title: 'See winners with recommendations',
        description:
          'Quickly see what’s working and we’ll guide you on what to make next.',
      },
      checkAdAccountHealthInSeconds: {
        title: 'Check ad account health in seconds',
        description:
          'Automatic snapshots of your account performance without the manual effort.',
      },
    },
  },
  joinOrg: {
    title: 'Your fellow wizards are already here!',
    description:
      'Someone in your team has already done the hard work and set up your organisation for MagicBrief with the domain <>{domain}<>',
    joinAction: 'Join Now',
    createAction: 'No, I want to <>create a new organisation<>',
    orgMembers: '{count} Members',
    errors: {
      getOrg:
        'An error occurred while trying to join the organisation, please try again or <>contact support.<>',
    },
  },
  learnMore: 'Learn more',
  learnMagic: 'Learn magic <>in 30 seconds<>',
  downloadExtension: 'Download Chrome Extension',
  mbExtension: 'MagicBrief Extension',
  facebookAdLibrary: 'Facebook Ad Library',
  tiktokAdLibrary: 'TikTok Ad Library',
  tiktokOrganic: 'TikTok Organic',
  tiktokCreativeCentre: 'TikTok Creative Centre',
  saveAds: 'Save ads from any platform',
  accessAds: 'Access ads',
  gettingStarted: 'Getting Started Guide',
  becomeWizard: 'Become a real wizard',
  authentication: {
    authInvite: "You're invited to join",
  },
  labels: {
    name: 'Name',
    title: 'Title',
    companyType: 'Company Type',
    companyName: 'Company Name',
    companyWebsite: 'Website',
    billingEmail: 'Billing Email',
    address: 'Address',
    whichBestDescribesYou: 'Which best describes you?',
    howDidYouHearAboutUs: 'How did you hear about us?',
    role: 'Role',
    teamSize: 'Team Size',
    logo: 'Logo',
    inviteLink: 'Invite Link',
    copyInvitationLink: 'Share invitation link',
    inviteByEmail: 'Email',
    email: 'Email address',
    inviteLinkRole: 'Invite Link Role',
    industry: 'Industry',
    profilePicture: 'Profile Picture',
    emailAddresses: 'Email Addresses',
    invite: 'Add user via email address',
    yourName: 'Your Name',
    workspaceName: 'Workspace Name',
    whichFeatures: 'Which features interest you?',
    uploadImage: 'Upload Image',
    noFileChosen: 'No file chosen',
    notSet: 'Not set',
  },
  placeholders: {
    companyName: 'My Company',
    companyWebsite: 'www.mycompany.com',
  },
  ad: {
    showDetails: 'Show Details',
    viewDetails: 'View Details',
    downloadAsset: 'Download Asset',
    copyThumbnail: 'Copy Thumbnail',
    copyThumbnailSuccess: 'Copied thumbnail to clipboard',
    copyThumbnailError: 'Error copying thumbnail: {error}',
    hideDetails: 'Hide Details',
    adCopy: 'Ad Copy',
    adDetails: 'Ad Details',
    ctaType: 'CTA Type',
    displayType: 'Display Type',
    likes: 'Likes',
    platforms: 'Platforms',
    categories: 'Categories',
    tags: 'Tags',
    notes: 'Notes',
    addNotes: 'Add Notes',
    downloadMP4: 'Download MP4',
    downloadPNG: 'Download PNG',
    downloadMedia: 'Download Media',
    landing: 'Landing Page',
    copyLink: 'Copy Ad Link',
    notesUnchanged: 'No changes',
    removeFromCollection: 'Remove From Collection',
    closeAdDetails: 'Close ad details',
    removeFromCollectionConfirm:
      'You can choose to remove this ad just from the collection "{collection}", or you can remove it entirely.',
    removeFromCollectionDetail:
      "Removing an ad entirely will delete it from yours and your team's saved ads and collections.",
    removeManyFromCollectionConfirm:
      'You can choose to remove these ads just from the collection "{collection}", or you can remove them entirely.',
    removeManyFromCollectionDetail:
      "Removing these ads entirely will delete them from yours and your team's saved ads and collections.",
    deleteAdConfirm:
      "Deleting an ad will delete it from yours and your team's saved ads and collections.",
    removeFromCollectionOnly: 'Remove From Collection Only',
    removeEverywhere: 'Delete Everywhere',
    deleteAd: 'Delete Ad',
    removeFromDiscover: 'Remove from Discover',
    removeBrand: 'Hide Brand Ads',
    removeBrandAdsFromDiscover: 'Hide Brand Ads From Discover',
    create: 'Create',
    errors: {
      addTagError:
        "We weren't able to tag this ad. We will look into this. The error received was: {error}",
      deleteFolderError:
        "We weren't able to delete this folder. We will look into this. The error received was: {error}",
      addManyTagsError:
        "We weren't able to tag these ads. We will look into this. The error received was: {error}",
      removeTagError:
        "We weren't able to remove that tag from this ad. We will look into this. The error received was: {error}",
      removeFromCollectionError:
        "We couldn't remove this ad from the collection. We will look into this. The error received was: {error}",
      removeManyAdsFromCollectionError:
        "We couldn't remove these ads from the collection. We will look into this. The error received was: {error}",
      addToCollectionError:
        "We couldn't save this ad to the collection. We will look into this. The error received was: {error}",
      addManyAdsToCollectionError:
        "We couldn't save these ads to the collections. We will look into this. The error received was: {error}",
      saveAdError:
        'Something went wrong saving this ad. We will look into this. The error received was: {error}',
      deleteManyAdsError:
        "We couldn't delete these ads. We will look into this. The error received was: {error}",
      addManyThemesError:
        "We weren't able to add themes to these ads. We will look into this. The error received was: {error}",
    },
    success: {
      deleteManyAds: 'Ads deleted',
      removeManyAds: 'Ads removed',
      deleteFolder: 'Folder deleted',
    },
    actions: 'Actions',
    industry: 'Industry',
    format: 'Format',
    tagInput: 'Search or create tags',
    boardSearchInput: 'Search or create board',
    folderInput: 'Search or create folder',
    createTag: 'Create new tag "{name}"',
    createFolder: 'Create new folder "{name}"',
    confirm: 'Confirm',
    createCollection: 'Create new collection "{name}"',
    save: 'Save',
    saved: 'Saved',
    addTag: 'Add tag',
    addTagToAds: 'Add tag to {adCount} ads',
    addTagsSuccess: 'Added tags to {adCount} ads',
    searchThemes: 'Select themes...',
    addTheme: 'Add theme',
    addThemeToAds: 'Add theme to {adCount} ads',
    addThemesSuccess: 'Added theme to {adCount} ads',
    addToCollection: 'Add to collection',
    addAdsToCollection: 'Add {adCount} ads to collections',
    addCollectionsSuccess: 'Added to {collectionCount} collections',
    removeAds: 'Remove {adCount} ads',
    removedFromDiscover: 'Removed from Discover',
    lifeCycle: {
      adScore: 'Performance Estimate',
      active: 'Active',
      inactive: 'Inactive',
      unlock: 'Unlock',
    },
    runTime: 'Run Time',
    launched: 'Launched',
    sortOptions: {
      adScore: 'Performance',
      adLongevity: 'Run Time',
      createdAt: 'Newest Ads',
      platformStartedAt: 'Newest Ads',
    },
  },
  magicAI: {
    magicAI: 'MagicAI',
    selectAnAsset: 'Select a video asset on this ad',
    frames: 'AI Frames',
    viewKeyframes: 'View keyframes',
    loading: 'Loading MagicAI...',
    unavailable: 'MagicAI not available',
    exportScript: 'Export Script',
    exportFrames: 'Export Frames',
    exportOptions: 'Export Options',
    copyScript: 'Copy Script',
    exportToBoard: 'Export To New Concept',
    exportSuccess: 'Export Success',
    exportError:
      'An error occurred while exporting from MagicAI to a new concept.',
    fallbackExportTitle: 'MagicAI Ad',
    defaultTitle: "{name}'s Ad",
  },
  packs: {
    edit: {
      title: 'Edit collection details',
      namePlaceholder: 'Enter a collection name',
      descriptionPlaceholder: 'Enter a short description',
      success: 'Ad collection updated',
      edit: 'Edit',
    },
    delete: {
      title: 'Delete collection',
      success: 'Ad collection deleted',
    },
    create: {
      title: 'Create ad collection',
      namePlaceholder: 'Enter a collection name',
      thumbnailPlaceholder: 'Upload thumbnail',
      descriptionPlaceholder: 'Enter a short description',
      success: 'Ad collection created',
      create: 'Create',
      error: 'Collection must be set to Public',
      thumbnailUploadFailed: 'Failed to upload thumbnail',
    },
    adCount: '{adCount} ads',
    filter: 'Filter',
    viewPack: 'View Collection',
    emptySearch:
      "There aren't any results matching the search '{query}'. Try adjusting your search terms and try again.",
    clearSearch: 'Clear Search',
  },
  brands,
  radar: {
    refresh: 'Refresh',
  },
  adPicker: {
    title: 'Select Ads',
  },
  boardPicker: {
    title: 'Select Concepts',
    numBoardsSelected: '{count} {{Concept|Concepts}} Selected',
  },
  enterDetails:
    'Enter your details to get started, you’ll be charged ${dollars}/month (sales tax included)',
  roles,
  industries,
  companyTypes,
  add: 'Add',
  new: 'New',
  done: 'Done',
  none: 'None',
  open: 'Open',
  share: 'Share',
  cancel: 'Cancel',
  close: 'Close',
  settings: 'Settings',
  apply: 'Apply',
  sortBy: 'Sort By',
  clear: 'Clear all',
  save: 'Save',
  saved: 'Saved',
  saveAndClose: 'Save & Close',
  proceed: 'Proceed',
  loading: 'Loading...',
  saving: 'Saving...',
  failed: 'Failed',
  cancelled: 'Cancelled',
  upload: 'Upload',
  uploading: 'Uploading...',
  disconnect: 'Disconnect',
  openLink: 'Open Link',
  uploadBox: 'Add files by dragging them here, or click here.',
  uploadImage: 'Add an image by dragging it here, or click here.',
  uploadMedia: 'Add images and videos by dragging them here, or click here.',
  uploadBoxDragActive: 'Drop files here...',
  uploadFilesByDrop: 'Drop files or folders to upload',
  queued: 'Queued',
  private: 'Private',
  public: 'Public',
  team: 'Team',
  move: 'Move',
  pending: 'Pending',
  remove: 'Remove',
  delete: 'Delete',
  deleteAll: 'Delete All',
  confirm: 'Confirm',
  duplicate: 'Duplicate',
  noneSelected: 'None selected',
  deleteWithName: 'Delete {name}',
  invite: 'Invite',
  rename: 'Rename',
  renameWithName: 'Rename {name}',
  moveToFolder: 'Move to folder',
  unknown: 'Unknown',
  filters: 'Filters',
  inviteAndFinish: 'Invite & Finish',
  inviteYourTeam: 'Invite Your Team',
  inviteModal: {
    title: 'Save ads together',
    description:
      'Make magic together, build out new concepts, creative briefs or source reference ads together.',
    sendInvites: 'Send Invites',
    heroTitle: 'Summer Campaign',
    heroSubtitle: '67 Ads Saved',
  },
  addAnother: 'Add another',
  optional: '(Optional)',
  edit: 'Edit',
  submit: 'Submit',
  copyLink: 'Copy Link',
  copyScript: 'Copy Script',
  copy: 'Copy',
  shareLink: 'Share Link',
  copyEmbedCode: 'Copy Embed Code',
  copyEmbedCodeToClipboard: 'Copy embed code to clipboard',
  embed: 'Embed',
  url: 'URL',
  shareItem: 'Share {name}',
  shareBoardLink: 'Share Concept Link',
  revokeLink: 'Revoke Link',
  regeneratingLink: 'Re-generating link...',
  revokeLinkSuccess: 'The old link was revoked and a new link was generated.',
  copiedToClipboard: 'Copied to clipboard',
  signedInAs: 'Signed in as',
  currentWorkspace: 'Current Workspace',
  yourProfile: 'Your Profile',
  workspace: 'Workspace',
  getExtension: 'Get Chrome Extension',
  chromeExtension: 'Use Chrome Extension',
  productCategories,
  workspaceSwitcher: {
    title: 'Switch Workspace',
    current: 'Current',
    none: 'No other workspaces to display.',
    switching: 'Switching to {name}...',
    filterOrganisations: 'Filter organisations...',
    noResults: 'No results found',
  },
  productUpdates: 'Product Updates',
  help: 'Help',
  signOut: 'Sign out',
  try: 'Try MagicBrief',
  libraryTryEntice: 'Save ad references in seconds!',
  privacyPolicy: 'Privacy policy',
  termsOfUse: 'Terms of use',
  billing: {
    addInfo: 'Add Billing Info',
  },
  retry: 'Retry',
  searchPlaceholder: 'Search...',
  searchLocal: 'Search {name}...',
  // eslint-disable-next-line no-useless-escape, prettier/prettier
  searchNamedPlaceholder: '{{Search...|Search ??...}}',
  errors: {
    genericWithDetail: 'Something went wrong: {detail}',
    genericTitle: 'Something went wrong',
    documentSyncUnexpected:
      'An unexpected error occurred while synchronizing the document with our server. Our engineers have been notified. In the meantime we strongly suggest reloading the page.',
    fieldRequired: 'This field is required.',
    permissionDenied: 'That action is not allowed.',
    adBlocker:
      'Your ad blocker may be blocking some of the content on this page. Try disabling it then reload.',
    billingUrlGenError:
      'Something went wrong while trying to redirect you to our billing portal. Please try again and contact support if the issue persists.',
    affiliateUrlGenError:
      'Something went wrong trying to redirect you to our affiliate page. Please try again and contact support if the issue persists',
    invalidEmail: 'Email address is invalid.',
    invalidEmailOrRepeated: 'Email address is invalid or already added.',
    incorrectPassword: 'Password is incorrect.',
    notAllowed: 'That action is not allowed.',
    writeClipboardError:
      "We couldn't copy the link to your clipboard. You can still copy it manually. Check your browser settings to ensure MagicBrief has clipboard permissions.",
    tooManyAttempts: 'Too many attempts. Please try again later.',
  },
  beta: 'Beta',
  shareCollection: 'Share Collection',
  upgradeToUnlock: 'Upgrade to \nunlock winning ads',
  unlock: 'Unlock',
  unlockAd: 'Unlock Ad',
  unlockAdPack: 'Unlock Ad Collection',
  addNew: 'Add New',
  newBoard: 'New Board',
  newAdPack: 'New Ad Collection',
  storyboard: {
    sidebar: {
      comments: { title: 'Comments' },
      close: { details: 'Close Comments' },
    },
    deleteConcept: 'Delete Concept',
  },
  brief: {
    sidebar: {
      comments: { title: 'Comments' },
      close: { details: 'Close Comments' },
    },
    dueIn: 'Due {countdown}',
  },
  extension: {
    loginError: 'An error occurred while authorizing extension: {detail}',
  },
  testimonial: {
    jed: 'Jed Ellington',
    cubbyCreative: 'Cubby Creative',
    jedTestimonial:
      '"MagicBrief pays for itself almost instantly. We ship more winners every month after taking inspiration from the ad library."',
  },
  saveCollection: 'Save Collection',
  collectionSaved: 'Collection saved',
  selected: '{selectedCount} selected',
  download: 'Download',
  downloadAll: 'Download All',
  thumbnail: 'Thumbnail',
  viewLP: 'View LP',
  details: 'Details',
  status: 'Status',
  theme: 'Theme',
  savedBy: 'Saved by {name}',
  editedAgo: 'Edited {time} ago',
  savedAgo: 'Saved {time} ago',
  month: 'month',
  custom: 'Custom',
  unlimited: 'Unlimited',
  free: 'Free',
  yes: 'Yes',
  no: 'No',
  numItemsSelected: '{count} {{Item|Items}} Selected',
  continue: 'Continue',
  or: 'Or',
  loadMore: 'Load More',
  primary: 'Primary',
  lastUpdatedOn: 'Last updated on',
  expires: 'Expires',
  endingWith: 'Ending with',
  setAsDefault: 'Set as default',
  expired: 'Expired',
  back: 'Back',
  connected: 'Connected',
  preview: 'Preview',
  activate: 'Activate',
  comingSoon: 'Coming soon',
  confirmSelection: 'Confirm Selection',
  getStarted: 'Get started',
  assets,
  briefs,
  comments,
  cta,
  directories,
  general,
  insights,
  integrations,
  library,
  login,
  navigation,
  notifications,
  onboarding,
  organisations,
  permissions,
  profileSettings,
  slack,
  template,
  tikTok,
  youtube,
  workspaceSettings,
};

export default en;
